import { WIDTH_360, WIDTH_400 } from '../../shared/configs/dialog-config';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { format, parse } from 'date-fns';
import pl from 'date-fns/locale/pl';
import { BehaviorSubject, combineLatest, merge, Observable, of, race, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, startWith, switchMap, tap } from 'rxjs/operators';
import { LiabilitiesDelegateService } from 'src/app/shared/services/liabilities-delegate.service';
import { AnalyticsEvent, AnalyticsService } from '../../core/services/analytics-service';
import { ApiService } from '../../core/services/api.service';
import { DialogService } from '../../core/services/dialog.service';
import { ToolbarService } from '../../core/services/toolbar.service';
import {
  DeleteSingleAutomaticPaymentConfirmationDialogComponent,
} from '../components/delete-single-automatic-payment-confirmation-dialog/delete-single-automatic-payment-confirmation-dialog.component';
import { EditLiabilityDialogComponent } from '../components/edit-liability-dialog/edit-liability-dialog.component';
import {
  SingleAutomaticPaymentCreatedDialogComponent,
} from '../components/single-automatic-payment-created-dialog/single-automatic-payment-created-dialog.component';
import { DueMonth } from '../models/due-month.model';
import { LiabilityAutomaticPaymentEnabled } from '../models/liability-automatic-payment-enabled.model';
import { Liability } from '../models/liability.model';
import { SearchLiabilities } from '../models/search-liabilities.model';
import { LiabilityApiService } from './liability-api.service';
import { UrgentLiabilitiesService } from './urgent-liabilities.service';
import { AutomaticReadingResultData } from '../models/automatic-reading-result-data';
import { AddLiabilityScanData } from '../models/add-liability-scan-data.model';
import { LiabilityStatus } from '../models/model';
import { CheckedLiabilitiesStoreService } from '../../shared/services/checked-liabilities.service';

@Injectable({
  providedIn: 'root',
})
export class LiabilityService {
  public search$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public data$: Observable<SearchLiabilities>;
  public refresh$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public tagIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  private count$: BehaviorSubject<number> = new BehaviorSubject<number>(20);
  public liabilitiesData$: BehaviorSubject<SearchLiabilities> = new BehaviorSubject(null);
  private dueMonths: DueMonth[];
  private searchFuture = false;
  private frameCopy;

  constructor(
    private api: ApiService,
    private analyticsService: AnalyticsService,
    private liabilityApiService: LiabilityApiService,
    private snackbar: MatSnackBar,
    private dialogService: DialogService,
    private toolbar: ToolbarService,
    private urgentLiabilitiesService: UrgentLiabilitiesService,
    private liabilitiesDelegateService: LiabilitiesDelegateService,
    private checkedLiabilitiesStoreService: CheckedLiabilitiesStoreService,
  ) {
    const tags$ = race(this.tagIds$, this.toolbar.tagIds$)
      .pipe(
        switchMap((t: string[]) => this.filterDueMonthsWithTags(t)));

    const searchParameters$ = combineLatest([this.searchSubject(), this.count$, tags$])
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        switchMap(([v, _, _2]: [string, number, string[]]) => this.filterMatchingLiabilities(v)),
      );

    const searchParametersMergedWithLiabilitiesCopy$ = merge(this.liabilitiesData$, searchParameters$);

    const withWebsocket$ = combineLatest([
      searchParametersMergedWithLiabilitiesCopy$,
      this.api.subscribe('liability').pipe(startWith('')),
    ]);

    this.data$ = withWebsocket$.pipe(
      filter(() => !!this.dueMonths),
      map(([data, socketFrame]) => {
        if (socketFrame) {
          const frame = socketFrame as Liability;
          if (socketFrame !== this.frameCopy) {
            if (!!data && data.liabilities && data.liabilities.map(value => value.id).includes(frame.id)) {
              const exist = false;
              const dataCopy = this.getDataCopy(data, frame, exist);
              this.frameCopy = socketFrame;
              this.liabilitiesData$.next(dataCopy);
              return dataCopy;
            }
          }
          if (frame.newStatus === LiabilityStatus.DELETED || frame.newStatus === LiabilityStatus.PAID_ON_PLATFORM
            || frame.newStatus === LiabilityStatus.PAID_OUTSIDE_PLATFORM) {
            this.checkedLiabilitiesStoreService.remove(frame.id);
          }
        }


        return data;
      }),
      switchMap((data) => this.initializeLiabilities(data, this.dueMonths)
        .pipe(map((data) => this.setXsellOfferVisible(data)))),
    );
  }

  private filterDueMonthsWithTags(tagIds: string[]): Observable<string[]> {
    return this.liabilityApiService.getDueMonths(tagIds).pipe(
      tap((d) => (this.dueMonths = d)),
      map(() => tagIds),
    );
  }

  public getLiabilityWithSubscription(liabilityId: string): Observable<Liability> {
    return this.refresh$.pipe(switchMap(() => this.liabilityApiService.getLiabilityWithSubscription(liabilityId)));
  }

  private getDataCopy(data, updatedLiability: Liability, exist: boolean): SearchLiabilities {
    return {
      ...data,
      months: data?.months?.map((month) => {
        const monthFormatted = format(parse(month.month, 'MMMM', new Date(), { locale: pl }), 'MM');

        const includedInMonth = updatedLiability.paymentDue.includes(`${month.year}-${monthFormatted}`);

        let modifiedMonth = {
          ...month,
          liabilities: month.liabilities
            .map((liability) => {
              if (liability.id === updatedLiability.id) {
                exist = true;

                if (updatedLiability.status === 'DELETED') {
                  return null;
                } else if (updatedLiability.status === 'PAID_ON_PLATFORM' ||
                  updatedLiability.status === 'PAID_OUTSIDE_PLATFORM') {
                  updatedLiability.inCart = false;
                  updatedLiability.isSelected = false;
                  return updatedLiability;
                } else {
                  return updatedLiability;
                }
              }

              return liability;
            })
            .filter((item) => item),
        };

        if (includedInMonth && !exist) {
          modifiedMonth.liabilities.push(updatedLiability);

          modifiedMonth = {
            ...modifiedMonth,
            liabilities: modifiedMonth.liabilities.sort(
              (a, b) => new Date(b.paymentDue).getTime() - new Date(a.paymentDue).getTime(),
            ),
          };
        }

        return modifiedMonth;
      }),
    };
  }

  public findFirstVisibleDate(dueMonths: DueMonth[]): DueMonth {
    return dueMonths.filter((d) => d.visible)
      .sort((a, b) => (a.month < b.month ? -1 : 1))[0];
  }

  public findFirstVisibleDateIndex(dueMonths: DueMonth[]): number {
    return dueMonths.indexOf(this.findFirstVisibleDate(dueMonths));
  }

  public findLastVisibleDate(dueMonths: DueMonth[]): DueMonth {
    return dueMonths.filter((d) => d.visible)
      .sort((a, b) => (a.month > b.month ? -1 : 2))[0];
  }

  public findLastVisibleDateIndex(dueMonths: DueMonth[]): number {
    return dueMonths.indexOf(this.findLastVisibleDate(dueMonths));
  }

  public hide(liabilityId: string): Observable<any> {
    return this.liabilityApiService.hide(liabilityId);
  }

  public show(liabilityId: string): Observable<any> {
    return this.liabilityApiService.show(liabilityId);
  }

  public addManualLiabilityFromFile(file: File): Observable<any> {
    return this.liabilityApiService
      .addManualLiabilityFromFile(file)
      .pipe(tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('AddDragDropLiability'))));
  }

  public uploadDocumentsForAutomaticReading(files: File[]): Observable<string> {
    return this.liabilityApiService
      .uploadDocumentsForAutomaticReading(files)
      .pipe(tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('Upload documents for reading'))));
  }

  public getAutomaticReadingResult(automaticReadingId: string): Observable<AutomaticReadingResultData> {
    return this.liabilityApiService.getAutomaticReadingResult(automaticReadingId);
  }

  public isVerified(id: string): Observable<boolean> {
    return this.liabilityApiService.isVerified(id);
  }

  public getAutomaticReadingFileUrl(automaticReadingId: string): Observable<string> {
    return this.liabilityApiService.getAutomaticReadingFileUrl(automaticReadingId);
  }

  public addLiabilityFromAutomaticReading(id: string, addLiabilityScanData: AddLiabilityScanData): Observable<string> {
    return this.liabilityApiService
      .addLiabilityFromAutomaticReading(id, addLiabilityScanData)
      .pipe(tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('Liability added from automatic reading'))));
  }

  public deleteAutomaticReading(id: string): Observable<string> {
    return this.liabilityApiService.deleteAutomaticReading(id);
  }

  public downloadPdf(liabilityId: string): Observable<any> {
    return this.liabilityApiService.downloadPdf(liabilityId).pipe(
      tap((response) => {
        const win = window.open(response, '_blank');
        win.focus();
      }),
      catchError((err) => throwError(() => err)),
      finalize(() => this.analyticsService.trackEvent(new AnalyticsEvent('CreateLiabilityDocumentPreviewLink'))),
    );
  }

  public enableAutoPayment(liability: Liability): Observable<LiabilityAutomaticPaymentEnabled> {
    return this.liabilityApiService
      .enableAutoPayment(liability.id)
      .pipe(tap((data: LiabilityAutomaticPaymentEnabled) => this.showCreatedDialog(data, liability)));
  }

  public disableWithConfirmationDialog(liability: Liability): Observable<any> {
    return this.dialogService
      .open(DeleteSingleAutomaticPaymentConfirmationDialogComponent, {
        data: liability,
        width: WIDTH_400,
      })
      .afterClosed()
      .pipe(
        filter((closed) => closed),
        switchMap(() => this.liabilityApiService.disableAutoPayment(liability.id)),
      );
  }

  public edit(liability: Liability): Observable<any> {
    return this.dialogService
      .open(EditLiabilityDialogComponent, {
        data: liability,
        width: WIDTH_360,
      })
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        switchMap(({ editLiabilityData, liabilityId }) =>
          this.liabilityApiService.edit(editLiabilityData, liabilityId),
        ),
        tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('EditLiability'))),
      );
  }

  public markAsPaid(liabilityId: string): Observable<Liability[]> {
    return this.liabilityApiService.markAsPaid(liabilityId).pipe(
      tap((liabilities) => {
        this.refreshLiabilities(liabilities);
        this.analyticsService.trackEvent(new AnalyticsEvent('Markaspaid'));
      }),
    );
  }

  public markAsNotPaid(liabilityId: string): Observable<any> {
    return this.liabilityApiService.markAsNotPaid(liabilityId).pipe(
      tap((liabilities) => {
        this.refreshLiabilities(liabilities);
        this.analyticsService.trackEvent(new AnalyticsEvent('Markasnotpaid'));
      }),
    );
  }

  public deleteLiabilityDelegation(liability: Liability): Observable<any> {
    return this.liabilitiesDelegateService
      .showDeleteDialogForLiabilityType(liability.delegationData, liability.id)
      .pipe(tap((liability) => this.refreshLiabilities([liability])));
  }

  public loadMoreFuture(): Observable<SearchLiabilities> {
    if (this.search$.value) {
      this.searchFuture = true;
      this.count$.next(this.count$.value + 10);
      return of(null);
    } else {
      const lastVisibleDateIndex = this.findLastVisibleDateIndex(this.dueMonths);

      if (lastVisibleDateIndex > 0 && lastVisibleDateIndex < this.dueMonths.length - 1) {
        const lastVisibleDate = this.loadLastVisibleDueMonth(lastVisibleDateIndex);

        return this.liabilityApiService
          .getLiabilities(this.findFirstVisibleDate(this.dueMonths).month, lastVisibleDate.month, this.tagIds$.value)
          .pipe(tap((data) => this.liabilitiesData$.next(data)));
      } else {
        return of(null);
      }
    }
  }

  public remindAboutPayment(liability: Liability): Observable<any> {
    return this.liabilityApiService.remindAboutPayment(liability.id).pipe(
      tap(() => {
        this.refreshLiabilities([liability]);
        // this.snackbar.open('Przypomnienie wysłane do odbiorcy. Możesz je ponowić za 24 godziny.', null, {
        //   duration: 5000,
        // });
        this.analyticsService.trackEvent(new AnalyticsEvent('Liability payment reminded', null));
      }),
    );
  }

  private loadLastVisibleDueMonth(lastVisibleDateIndex: number): DueMonth {
    let lastVisibleDate = this.dueMonths[lastVisibleDateIndex + 1];
    let liabilitiesToLoad = 10 - lastVisibleDate.liabilitiesCount;
    lastVisibleDateIndex += 1;
    lastVisibleDate.visible = true;

    while (liabilitiesToLoad > 0 && lastVisibleDateIndex > 0 && lastVisibleDateIndex < this.dueMonths.length - 1) {
      lastVisibleDateIndex += 1;
      lastVisibleDate = this.dueMonths[lastVisibleDateIndex];
      lastVisibleDate.visible = true;
      liabilitiesToLoad -= lastVisibleDate.liabilitiesCount;
    }
    return lastVisibleDate;
  }

  public loadMorePast(): Observable<SearchLiabilities> {
    if (this.search$.value) {
      this.count$.next(this.count$.value + 10);
      return of(null);
    } else {
      const firstVisibleIndex = this.findFirstVisibleDateIndex(this.dueMonths);

      if (firstVisibleIndex > 0) {
        const firstVisibleDate = this.loadFirstVisibleDueMonth(firstVisibleIndex);

        return this.liabilityApiService
          .getLiabilities(firstVisibleDate.month, this.findLastVisibleDate(this.dueMonths).month, this.tagIds$.value)
          .pipe(tap((data) => this.liabilitiesData$.next(data)));
      } else {
        return of(null);
      }
    }
  }

  private loadFirstVisibleDueMonth(firstVisibleIndex: number): DueMonth {
    let firstVisibleDueMonth = this.dueMonths[firstVisibleIndex - 1];
    let liabilitiesToLoad = 10 - firstVisibleDueMonth.liabilitiesCount;
    firstVisibleDueMonth.visible = true;
    firstVisibleIndex -= 1;

    while (liabilitiesToLoad > 0 && firstVisibleIndex > 0) {
      firstVisibleIndex -= 1;
      firstVisibleDueMonth = this.dueMonths[firstVisibleIndex];
      firstVisibleDueMonth.visible = true;
      liabilitiesToLoad -= firstVisibleDueMonth.liabilitiesCount;
    }
    return firstVisibleDueMonth;
  }

  private refreshLiabilities(liabilities: Liability[]): void {
    if (this.liabilitiesData$.value) {
      this.liabilitiesData$.next(this.getDataCopy(this.liabilitiesData$.value, liabilities[0], true));
    }
    this.urgentLiabilitiesService.refresh.next(true);
    this.refresh$.next(true);
  }

  public refreshLiability(liability: Liability): void {
    this.liabilitiesData$.next(this.getDataCopy(this.liabilitiesData$.value, liability, true));
    this.urgentLiabilitiesService.refresh.next(true);
    this.refresh$.next(true);
  }


  private showCreatedDialog(
    data: LiabilityAutomaticPaymentEnabled,
    liability: Liability,
  ): MatDialogRef<SingleAutomaticPaymentCreatedDialogComponent> {
    return this.dialogService.open(SingleAutomaticPaymentCreatedDialogComponent, {
      data: {
        card: data.creditCardVM,
        liability,
      },
      width: '457px',
    });
  }

  private initializeLiabilities(data: SearchLiabilities, dueMonths: DueMonth[]): Observable<SearchLiabilities> {

    if (data) {
      if (dueMonths && dueMonths.length > 0 && data) {
        let visibled = dueMonths.filter(value => value.visible);
        data.hasLiabilities = visibled.length > 0;
      }
      return of(data);
    }

    if (dueMonths && dueMonths.length > 0) {
      return this.liabilityApiService.getLiabilities(
        this.findFirstVisibleDate(dueMonths).month,
        this.findLastVisibleDate(dueMonths).month,
      ).pipe(
        map(value => {
          value.hasLiabilities = value.months.length > 0;
          return value;
        }),
      );
    }

    const liabilities = new SearchLiabilities();
    liabilities.months = [];
    liabilities.hasMoreFuture = false;
    liabilities.hasMorePast = false;
    liabilities.liabilities = [];
    return of(liabilities);
  }

  private searchSubject(): Observable<string> {
    return this.search$.pipe(debounceTime(100));
  }

  private filterMatchingLiabilities(query: string): Observable<SearchLiabilities> {
    const tagIds = this.getTagIds();
    if (query) {
      query = query.replace(/\+/g, '');
      const params = {
        query,
        count: '' + this.count$.value,
        searchFuture: '' + this.searchFuture,
        tagId: tagIds,
      };
      this.searchFuture = false;
      return this.api.get<SearchLiabilities>(`/liabilities/search`, params);
    }
    if (tagIds) {
      const firstVisibleDate = this.findFirstVisibleDate(this.dueMonths);
      if (firstVisibleDate) {
        return this.liabilityApiService.getLiabilities(
          firstVisibleDate.month,
          this.findLastVisibleDate(this.dueMonths).month,
          tagIds,
        );
      } else {
        const searchLiabilities = new SearchLiabilities();
        searchLiabilities.months = [];
        searchLiabilities.hasMorePast = false;
        searchLiabilities.hasMoreFuture = false;
        searchLiabilities.hasBillers = false;
        return of(searchLiabilities);
      }
    }
    return this.liabilitiesData$;
  }

  private getTagIds(): string[] {
    if (this.tagIds$.value.length > 0) {
      return this.tagIds$.value;
    } else if (this.toolbar.tagIds$.value.length > 0) {
      return this.toolbar.tagIds$.value;
    }
    return [];
  }

  private setXsellOfferVisible(liabilities: SearchLiabilities): SearchLiabilities {
    if (!liabilities) {
      return liabilities;
    }
    const shownXsellOffers = [];
    liabilities?.months?.forEach((month) => {
      month.liabilities
        ?.filter((liability) => liability.xsellOffer || liability.showXsellConsentBtn)
        ?.forEach((liability) => {
          if (!shownXsellOffers.includes(liability.customerBillerId)) {
            liability.showXsellConsentBtn = true;
            shownXsellOffers.push(liability.customerBillerId);
          } else {
            liability.showXsellConsentBtn = false;
          }
        });
    });

    return liabilities;
  }

  public getPayableLiabilities(): Observable<string[]> {
    return this.api.get<string[]>(`/liabilities/payable`);
  }
}
